import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Directive, Input, NgModule } from '@angular/core';
import { CSSComponent, CSSModifier } from '@odx/angular/internal';
import { injectElement } from '@odx/angular/utils';
import * as i1 from '@odx/angular';
import { DisabledController, WithDisabledState, CoreModule } from '@odx/angular';

/**
 * The `BarComponent` serves as a UI container component, typically used for presenting navigational bars,
 * toolbars, or information bars within an application. Its design is easily customizable through the use of CSS classes.
 *
 * This component is standalone, meaning it can be used independently of other components without requiring explicit imports
 * of other modules, making it easy to include wherever a bar-like structure is needed in the application.
 */
let BarComponent = class BarComponent {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: BarComponent, isStandalone: true, selector: "odx-bar", ngImport: i0, template: "<ng-content select=\"button[odxBarButton], [odxBarLabel]\" />\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
BarComponent = __decorate([
    CSSComponent('bar')
], BarComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, selector: 'odx-bar', template: "<ng-content select=\"button[odxBarButton], [odxBarLabel]\" />\n" }]
        }] });

const BarButtonVariant = {
    DEFAULT: 'default',
    SMALL: 'small',
};

/**
 * Represents a directive for a bar button.
 */
let BarButtonDirective = class BarButtonDirective {
    constructor() {
        this.disabledController = DisabledController.inject();
        this.element = injectElement();
        /**
         * The variant of the bar button.
         *
         * @type {BarButtonVariant}
         * @default BarButtonVariant.DEFAULT
         */
        this.variant = BarButtonVariant.DEFAULT;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: BarButtonDirective, isStandalone: true, selector: "button[odxBarButton]", inputs: { variant: "variant" }, hostDirectives: [{ directive: i1.WithDisabledState }], ngImport: i0 }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], BarButtonDirective.prototype, "variant", void 0);
BarButtonDirective = __decorate([
    CSSComponent('bar-button')
], BarButtonDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[odxBarButton]',
                    standalone: true,
                    hostDirectives: [WithDisabledState],
                }]
        }], propDecorators: { variant: [{
                type: Input
            }] } });

/**
 * Represents a directive for displaying labels in a bar component.
 */
let BarLabelDirective = class BarLabelDirective {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: BarLabelDirective, isStandalone: true, selector: "[odxBarLabel]", ngImport: i0 }); }
};
BarLabelDirective = __decorate([
    CSSComponent('bar-label')
], BarLabelDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[odxBarLabel]',
                    standalone: true,
                }]
        }] });

const modules = [BarComponent, BarButtonDirective, BarLabelDirective];
class BarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: BarModule, imports: [BarComponent, BarButtonDirective, BarLabelDirective], exports: [CoreModule, BarComponent, BarButtonDirective, BarLabelDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarModule, imports: [CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BarButtonDirective, BarButtonVariant, BarComponent, BarLabelDirective, BarModule };
